import React from 'react';
import { Tooltip } from '@mui/material';
import { Error } from '@mui/icons-material';
import type {
  FormikValues,
  useFormik,
  FormikErrors,
  FormikTouched,
} from 'formik';
import type { DimensionData } from './types';
import { EVALUATION_TEXT } from './lang';
import { TextField } from '../../../../../components';
import { handleNumberKeyDown } from '../../../../../utils';

export const ScoreHeader: React.FC = () => (
  <div
    className="flex flex-row w-full grid grid-cols-3"
    style={{ gridTemplateColumns: '4fr 2fr 3fr' }}
  >
    <span />
    <span className="flex flex-row items-start justify-center font-bold">
      {'AI Score'}
    </span>
    <span className="font-bold">{'Modified Score'}</span>
  </div>
);

type Formik<T extends FormikValues> = ReturnType<typeof useFormik<T>>;
type DimensionBlockProps<T extends FormikValues> = {
  dimensionIndex: number;
  dimensionFormik: Formik<T>;
  dimensionEditable: boolean;
  textStyle: string;
  dimensionName: string;
};

export const DimensionBlock = <T extends FormikValues>({
  dimensionIndex,
  dimensionFormik,
  dimensionEditable,
  textStyle,
  dimensionName,
}: DimensionBlockProps<T>): React.ReactNode => {
  const {
    systemScore,
    overrideScore,
    totalScore,
    remark = '',
  } = dimensionFormik.values.dimensions[dimensionIndex];

  const touchedDimensions = dimensionFormik.touched?.dimensions as Array<
    FormikTouched<DimensionData>
  >;
  const errorsDimensions = dimensionFormik.errors?.dimensions as Array<
    FormikErrors<DimensionData>
  >;

  const error =
    Boolean(touchedDimensions) &&
    touchedDimensions[dimensionIndex]?.overrideScore &&
    Boolean((errorsDimensions ?? [])[dimensionIndex]?.overrideScore ?? false);

  const helperText =
    Boolean(touchedDimensions) &&
    touchedDimensions[dimensionIndex]?.overrideScore &&
    (errorsDimensions ?? [])[dimensionIndex]?.overrideScore;

  const hasRemark = false; // hide remark for now

  return (
    <>
      <span className={textStyle}>{dimensionName}</span>
      <span className="flex flex-row items-center justify-center text-[#951D1D] mt-2">
        {systemScore}
        {hasRemark && (
          <span className="flex flex-row items-start">
            ({remark}
            <Tooltip title={EVALUATION_TEXT.ACADEMIC_DIMENSION_TOOLTIP}>
              <Error
                sx={{
                  fontSize: '0.8rem',
                }}
              />
            </Tooltip>
            )
          </span>
        )}
        &nbsp; / {totalScore}
      </span>
      <span className="flex flex-row items-start">
        <TextField
          name={`dimensions.${dimensionIndex}.overrideScore`}
          value={overrideScore}
          onChange={dimensionFormik.handleChange}
          onBlur={dimensionFormik.handleBlur}
          size="small"
          disabled={!dimensionEditable}
          sx={{
            marginRight: '0.5rem',
            '.MuiInputBase-root': {
              bgcolor: 'white',
            },
          }}
          type="number"
          inputProps={{
            step: 1,
            pattern: '[0-9]*',
          }}
          onKeyDown={handleNumberKeyDown}
          error={error}
          helperText={helperText}
        />
        <span className="w-16 mt-2">{'/ ' + totalScore}</span>
      </span>
    </>
  );
};
